import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { GlassModal } from '@/components/ui/glass-modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PRICE_INPUT_PROPS } from '@/lib/priceInputProps';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/lib/toast';
import { insertChatMessage } from '@/queries/chat';
import { counterOfferInterest, createOfferInterest } from '@/queries/offers';
import { SellerContactNoteCallout } from '@/components/inquiry/SellerContactNoteCallout';
import { formatUsdCents } from '@/lib/formatMoney';


interface MakeOfferSheetProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	conversationId: string;
	itemId: string | null | undefined;
	itemTitle: string | null | undefined;
	currentAskingCents?: number | null;
	currentUserId: string | undefined;
	/** When set, treat this as a counter to an existing interest. */
	counterTo?: { interestId: string; previousCents: number } | null;
	/** Seller's @handle — surfaces their Contact Note above the amount field. */
	sellerUsername?: string | null;
}

/**
 * Glass-band modal for "Make offer" or "Counter". Writes to item_interests
 * AND posts a chat_messages card so both surfaces stay in sync.
 */
export const MakeOfferSheet = ({
	open,
	onOpenChange,
	conversationId,
	itemId,
	itemTitle,
	currentAskingCents,
	currentUserId,
	counterTo,
	sellerUsername,
}: MakeOfferSheetProps) => {
	const isCounter = !!counterTo;
	const [amount, setAmount] = useState<string>('');
	const [note, setNote] = useState('');
	const [busy, setBusy] = useState(false);

	useEffect(() => {
		if (!open) return;
		if (counterTo) {
			setAmount((counterTo.previousCents / 100).toFixed(2));
		} else if (currentAskingCents) {
			setAmount((currentAskingCents / 100).toFixed(2));
		} else {
			setAmount('');
		}
		setNote('');
	}, [open, counterTo, currentAskingCents]);

	const submit = async () => {
		if (!currentUserId || !itemId) {
			toast.error('Missing context');
			return;
		}
		const cents = Math.round(parseFloat(amount) * 100);
		if (!Number.isFinite(cents) || cents <= 0) {
			toast.error('Enter a valid amount');
			return;
		}
		setBusy(true);
		try {
			const messageUid = crypto.randomUUID();
			if (isCounter && counterTo) {
				const { error } = await counterOfferInterest(counterTo.interestId, {
					counter_offer_cents: cents,
					counter_message: note || null,
				});
				if (error) throw error;

				await insertChatMessage({
					conversationId,
					senderId: currentUserId,
					kind: 'counter_offer',
					body: note ? note.slice(0, 280) : `Counter: ${formatUsdCents(cents)}`,
					metadata: {
						message_uid: messageUid,
						interest_id: counterTo.interestId,
						item_id: itemId,
						item_title: itemTitle,
						amount_cents: cents,
					},
				});
				toast.success('Counter-offer sent');
			} else {
				const { data: interest, error: iErr } = await createOfferInterest({
					itemId,
					senderId: currentUserId,
					offerCents: cents,
					message: note || null,
				});
				if (iErr) {
					if (iErr.code === '23505') throw new Error('You already have an active offer on this item');
					throw iErr;
				}

				await insertChatMessage({
					conversationId,
					senderId: currentUserId,
					kind: 'offer',
					body: note ? note.slice(0, 280) : `Offer: ${formatUsdCents(cents)}`,
					metadata: {
						message_uid: messageUid,
						interest_id: interest.id,
						item_id: itemId,
						item_title: itemTitle,
						amount_cents: cents,
					},
				});
				toast.success('Offer sent');
			}
			onOpenChange(false);
		} catch (err: any) {
			toast.error('Could not send', { description: err?.message });
		} finally {
			setBusy(false);
		}
	};

	return (
		<GlassModal
			open={open}
			onOpenChange={onOpenChange}
			title={isCounter ? 'Counter-offer' : 'Make an offer'}
			description={itemTitle || 'Item'}
		>
			<div className="space-y-1 text-left">
				<h2 className="text-lg font-semibold text-foreground">
					{isCounter ? 'Counter-offer' : 'Make an offer'}
				</h2>
				<p className="text-sm text-muted-foreground truncate">{itemTitle || 'Item'}</p>
			</div>

			<div className="space-y-4 py-4">
				<SellerContactNoteCallout sellerUsername={sellerUsername} />
				<div>
					<label className="text-[12px] font-medium text-muted-foreground mb-1.5 block">
						Amount (USD)
					</label>
					<div className="relative">
						<span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
							$
						</span>
						<Input
							type="text"
							inputMode="decimal"
							pattern="[0-9]*\.?[0-9]*"
							{...PRICE_INPUT_PROPS}
							value={amount}
							onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ''))}
							className="pl-7 text-lg tabular-nums"
							style={{ fontSize: 16 }}

						/>
					</div>
					{currentAskingCents && !isCounter && (
						<div className="text-[11.5px] text-muted-foreground mt-1.5">
							Asking {formatUsdCents(currentAskingCents)}
						</div>
					)}
				</div>
				<div>
					<label className="text-[12px] font-medium text-muted-foreground mb-1.5 block">
						Note (optional)
					</label>
					<Textarea
						value={note}
						onChange={(e) => setNote(e.target.value)}
						rows={3}
						placeholder="e.g. firm, can pick up today, etc."
						style={{ fontSize: 16 }}
					/>
				</div>
			</div>
			<div className="flex gap-2 pt-2">
				<Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)}>
					Cancel
				</Button>
				<Button className="flex-1" onClick={submit} disabled={busy}>
					{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : isCounter ? 'Send counter' : 'Send offer'}
				</Button>
			</div>
		</GlassModal>
	);
};

export default MakeOfferSheet;
